import ChevronIcon from "@/assets/colorable/chevron.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import CircleWithTickIcon from "@/assets/light_gradient/circle_with_tick.svg?react";
import ReusableDropdown from "@/components/Common/forms/ReusableDropdown";
import clsxm from "@/lib/clsxm";
import {PERMISSIONS} from "@/store/constants";
import type {TPermission} from "@/store/types";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const PermissionSelect: FC<{
  value: TPermission | null;
  onChange: (value: string) => void;
  excludedPermissions?: TPermission[];
  topOptions?: Array<{value: string; label: string}>;
  bottomOptions?: Array<{value: string; label: string}>;
  placeholder?: string;
  containerClassName?: string;
  dropdownClassName?: string;
  dataTestId?: string;
  dropDownIconVariant?: "OUTLINE" | "FILLED";
  renderButtonClassName?: string;
}> = ({
  value,
  onChange,
  excludedPermissions,
  topOptions = [],
  bottomOptions = [],
  placeholder,
  containerClassName,
  dataTestId,
  dropdownClassName,
  dropDownIconVariant = "OUTLINE",
  renderButtonClassName,
}) => {
  const {t} = useTranslation("multi_account");
  const defaultPermissionOptions = PERMISSIONS.filter(
    permission =>
      permission !== "REFLUENCED_COMPANY_ADMIN" &&
      permission !== "REFLUENCED_INFLUENCER_ADMIN" &&
      permission !== "ADMIN" &&
      !excludedPermissions?.includes(permission),
  ).map(permission => ({
    value: permission,
    label: t(`${permission.toLowerCase() as "write"}_permissions`),
  }));
  const dropdownOptions = [...topOptions, ...defaultPermissionOptions, ...bottomOptions];
  // The current value isn't always among the selectable options — e.g. WRITE/READ/PURCHASE
  // in the agency view (which only offers ADMIN), or ADMIN in the company view (which
  // excludes it). Fall back to a display-only option so the button always shows the
  // actual permission label instead of rendering blank.
  const selectedOption =
    dropdownOptions.find(permission => permission.value === value) ??
    (value ? {value, label: t(`${value.toLowerCase() as "write"}_permissions`)} : undefined);

  return (
    <ReusableDropdown
      shouldUseCssDivide={false}
      containerClassName={clsxm("min-w-45 md:max-w-46.25 ", containerClassName)}
      dropdownClassName={dropdownClassName}
      setSelectedOption={option => onChange(option.value)}
      selectedOption={selectedOption}
      options={dropdownOptions}
      renderButton={({selectedOption, isOpen, className, ...rest}) => (
        <button
          {...rest}
          data-testid={dataTestId}
          className={clsxm(
            className,
            selectedOption ? "text-neutral-black" : "text-neutral-grey-light",
            "border-primary-light-solid h-auto gap-2 px-4 py-2",
            renderButtonClassName,
          )}>
          <span className="grow items-center text-left text-xs">
            {!!selectedOption ? selectedOption?.label : placeholder}
          </span>
          {dropDownIconVariant === "OUTLINE" && (
            <RightChevronIcon
              className={clsxm(
                "text-neutral-black size-2 transition-transform duration-250",
                isOpen ? "rotate-270" : "rotate-90",
              )}
            />
          )}
          {dropDownIconVariant === "FILLED" && (
            <ChevronIcon
              className={clsxm(
                "text-primary-dark-solid mr-4 transition-transform duration-250",
                isOpen ? "rotate-180" : "rotate-0",
              )}
            />
          )}
        </button>
      )}
      renderItem={({option, isHighlighted, isSelected, className, ...rest}) => {
        return (
          <li
            {...rest}
            data-testid={dataTestId ? `${dataTestId}-option-${option.value.toLowerCase()}` : undefined}
            value={option.value}
            className={clsxm(
              className,
              isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
              rest.key === topOptions.length - 1
                ? "border-b-neutral-white-dark border-b"
                : !!bottomOptions.length && rest.key === topOptions.length + defaultPermissionOptions.length
                  ? "border-t-neutral-white-dark border-t"
                  : "",
              "h-auto gap-2 py-3",
            )}>
            {isSelected ? <CircleWithTickIcon className="size-3.5 shrink-0" /> : <div className="size-3.5 shrink-0" />}
            <span className="grow text-xs leading-none">{option.label}</span>
          </li>
        );
      }}
    />
  );
};

export default PermissionSelect;
